import {
  Row,
  Col,
  Input,
  Card,
  CardBody,
  CardHeader,
  Collapse,
} from "reactstrap"
import Select from "react-select"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { triStateOptionsWithCounts, getTriStateValue } from "helpers/filterHelpers"

export default function ContentFilters({
  params,
  updateParams,
  isAdvancedOpen,
  isFiltersOpen,
  setIsAdvancedOpen,
}) {
  const {
    isLoading,
    brands: brandsData,
    models: modelsData,
    years: yearsData,
    statuses: statusesData,
    locations: locationsData,
    showrooms: showroomsData,
    booleanCounts,
  } = useFilteredVehicleOptions(params)

  const buildOptions = data =>
    data.map(item => ({
      value: item.slug,
      label: item.label,
    }))

  const handleMultiSelect = (key, selected) => {
    const values = selected ? selected.map(s => s.value) : []
    updateParams(prev => ({
      ...prev,
      [key]: values.join(","),
      page: 1,
    }))
  }

  const getSelectedValues = (key, data) => {
    const values = params[key]?.split(",").filter(Boolean) || []
    return buildOptions(data).filter(opt => values.includes(opt.value))
  }

  const handleNumericChange = (key, value) => {
    updateParams(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }))
  }

  const handleSelectChange = (key, value) => {
    updateParams(prev => ({
      ...prev,
      [key]: value,
      page: 1,
    }))
  }

  const sortOptions = [
    { value: "created_at", label: "Date Created" },
    { value: "updated_at", label: "Last Updated" },
    { value: "asking_price", label: "Price" },
  ]

  const sortOrderOptions = [
    { value: "desc", label: "Descending" },
    { value: "asc", label: "Ascending" },
  ]

  const isPublishedOptions = triStateOptionsWithCounts("Published", "Unpublished", booleanCounts?.is_published?.published, booleanCounts?.is_published?.draft)
  const hasImagesOptions = triStateOptionsWithCounts("Has Images", "No Images", booleanCounts?.has_images?.with_images, booleanCounts?.has_images?.without_images)
  const hasPriceOptions = triStateOptionsWithCounts("Has Price", "No Price", booleanCounts?.has_price?.with_price, booleanCounts?.has_price?.without_price)
  const isSoldOptions = triStateOptionsWithCounts("Sold", "Not Sold", booleanCounts?.is_sold?.sold, booleanCounts?.is_sold?.not_sold)

  if (!isFiltersOpen) return null

  return (
    <div className="mb-3">
      {/* Common Filters */}
      <Row className="mb-3">
        <Col sm={12}>
          <small className="text-success">
            {isLoading ? "Option fetching..." : ""}
          </small>
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Brand</label>
          <Select
            isMulti
            value={getSelectedValues("brand", brandsData)}
            onChange={selected => handleMultiSelect("brand", selected)}
            options={buildOptions(brandsData)}
          />
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Model</label>
          <Select
            isMulti
            value={getSelectedValues("model", modelsData)}
            onChange={selected => handleMultiSelect("model", selected)}
            options={buildOptions(modelsData)}
          />
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Year</label>
          <Select
            isMulti
            value={getSelectedValues("year", yearsData)}
            onChange={selected => handleMultiSelect("year", selected)}
            options={buildOptions(yearsData)}
          />
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Status</label>
          <Select
            isMulti
            value={getSelectedValues("status", statusesData)}
            onChange={selected => handleMultiSelect("status", selected)}
            options={buildOptions(statusesData)}
          />
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Location</label>
          <Select
            isMulti
            value={getSelectedValues("location", locationsData)}
            onChange={selected => handleMultiSelect("location", selected)}
            options={buildOptions(locationsData)}
          />
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Showroom</label>
          <Select
            isMulti
            value={getSelectedValues("showroom", showroomsData)}
            onChange={selected => handleMultiSelect("showroom", selected)}
            options={buildOptions(showroomsData)}
          />
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Is Published</label>
          <Select
            value={getTriStateValue(isPublishedOptions, params.is_published)}
            onChange={selected =>
              handleSelectChange("is_published", selected.value)
            }
            options={isPublishedOptions}
          />
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Images</label>
          <Select
            value={getTriStateValue(hasImagesOptions, params.has_images)}
            onChange={selected =>
              handleSelectChange("has_images", selected.value)
            }
            options={hasImagesOptions}
          />
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Price</label>
          <Select
            value={getTriStateValue(hasPriceOptions, params.has_price)}
            onChange={selected =>
              handleSelectChange("has_price", selected.value)
            }
            options={hasPriceOptions}
          />
        </Col>

        <Col sm={6} lg={3} xl={2}>
          <label className="control-label">Sold</label>
          <Select
            value={getTriStateValue(isSoldOptions, params.is_sold)}
            onChange={selected =>
              handleSelectChange("is_sold", selected.value)
            }
            options={isSoldOptions}
          />
        </Col>
      </Row>

      {/* Advanced Filters */}
      <Card>
        <CardHeader
          style={{ cursor: "pointer" }}
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
          className="d-flex justify-content-between align-items-center"
        >
          <h6 className="mb-0">Advanced Filters</h6>
          <i className={`fas fa-chevron-${isAdvancedOpen ? "up" : "down"}`} />
        </CardHeader>
        <Collapse isOpen={isAdvancedOpen}>
          <CardBody>
            <Row>
              <Col sm={6} lg={3} xl={2}>
                <label className="control-label">Price Min</label>
                <Input
                  type="number"
                  value={params.price_min || ""}
                  placeholder="Min"
                  onChange={e =>
                    handleNumericChange("price_min", e.target.value)
                  }
                />
              </Col>

              <Col sm={6} lg={3} xl={2}>
                <label className="control-label">Price Max</label>
                <Input
                  type="number"
                  value={params.price_max || ""}
                  placeholder="Max"
                  onChange={e =>
                    handleNumericChange("price_max", e.target.value)
                  }
                />
              </Col>

              <Col sm={2}>
                <label className="control-label">Sort By</label>
                <Select
                  value={sortOptions.find(
                    opt => opt.value === (params.sort_by || "created_at")
                  )}
                  onChange={selected =>
                    handleSelectChange("sort_by", selected.value)
                  }
                  options={sortOptions}
                />
              </Col>

              <Col sm={2}>
                <label className="control-label">Sort Order</label>
                <Select
                  value={sortOrderOptions.find(
                    opt => opt.value === (params.sort_order || "desc")
                  )}
                  onChange={selected =>
                    handleSelectChange("sort_order", selected.value)
                  }
                  options={sortOrderOptions}
                />
              </Col>
            </Row>
          </CardBody>
        </Collapse>
      </Card>

      {/* Reset & Actions */}
      <Row className="mt-3">
        <Col sm={12}>
          <div className="d-flex gap-2 justify-content-between align-items-center">
            <div>
              <span className="text-muted">
                Showing{" "}
                {
                  ["brand", "model", "year", "status", "location", "showroom",
                    "is_published", "has_images", "has_price", "is_sold",
                    "price_min", "price_max"].filter(
                    key => params[key] !== "" && params[key] != null
                  ).length
                }{" "}
                active filters
              </span>
            </div>
            <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
            >
              {isAdvancedOpen ? "▲ Hide" : "▼ Show"} Advanced Filters
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() =>
                updateParams(prev => ({
                  ...prev,
                  brand: "",
                  model: "",
                  year: "",
                  status: "",
                  location: "",
                  showroom: "",
                  is_published: "",
                  has_images: "",
                  has_price: "",
                  is_sold: "",
                  price_min: "",
                  price_max: "",
                  sort_by: "created_at",
                  sort_order: "desc",
                  page: 1,
                }))
              }
            >
              Reset Filters
            </button>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}
